import React from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import './PrivacyPage.css';

const TermsPage: React.FC = () => {
  usePageTitle('Terms of Service');
  const lastUpdated = "May 7, 2026";

  return (
    <div className="privacy-page">
      <section className="privacy-hero">
        <div className="container">
          <span className="section-label">Legal</span>
          <h1>Terms of Service</h1>
          <p className="privacy-subtitle">
            The agreement that governs your access to and use of JuryView's services
          </p>
          <p className="last-updated">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="container">
          <div className="policy-wrapper">
            <nav className="policy-nav">
              <h4>Contents</h4>
              <ul>
                <li><a href="#acceptance">Acceptance of Terms</a></li>
                <li><a href="#eligibility">Eligibility</a></li>
                <li><a href="#accounts">Accounts & Credentials</a></li>
                <li><a href="#license">License & Subscription</a></li>
                <li><a href="#acceptable-use">Acceptable Use</a></li>
                <li><a href="#customer-data">Customer Data</a></li>
                <li><a href="#ai-outputs">AI Outputs & No Legal Advice</a></li>
                <li><a href="#ip">Intellectual Property</a></li>
                <li><a href="#fees">Fees & Payment</a></li>
                <li><a href="#term">Term & Termination</a></li>
                <li><a href="#warranties">Warranty Disclaimer</a></li>
                <li><a href="#liability">Limitation of Liability</a></li>
                <li><a href="#indemnification">Indemnification</a></li>
                <li><a href="#governing-law">Governing Law</a></li>
                <li><a href="#changes">Changes to Terms</a></li>
                <li><a href="#contact">Contact Us</a></li>
              </ul>
            </nav>

            <div className="policy-body">
              <section id="acceptance" className="policy-section">
                <h2>1. Acceptance of Terms</h2>
                <p>
                  These Terms of Service ("Terms") form a binding agreement between you and JuryView, Inc.
                  ("JuryView," "we," "us," or "our") and govern your access to and use of the JuryView
                  platform, applications, APIs, websites, and related services (collectively, the "Services").
                </p>
                <p>
                  By accessing or using the Services, by clicking to accept these Terms, or by signing an
                  order form or other agreement that incorporates these Terms, you agree to be bound by them.
                  If you are entering into these Terms on behalf of an organization, you represent that you
                  have authority to bind that organization, and "you" refers to that organization.
                </p>
                <p>
                  If you do not agree to these Terms, you may not access or use the Services.
                </p>
              </section>

              <section id="eligibility" className="policy-section">
                <h2>2. Eligibility & Authorized Users</h2>
                <p>
                  The Services are intended for use by legal professionals, trial consultants, insurance
                  carriers, legal technology providers, and other organizations engaged in litigation,
                  research, or related professional activities. To use the Services you must be at least
                  18 years old and capable of entering into a binding contract.
                </p>
                <p>
                  You may permit your employees, contractors, and other authorized personnel ("Authorized
                  Users") to access the Services on your behalf, provided each Authorized User complies
                  with these Terms. You are responsible for the acts and omissions of your Authorized Users.
                </p>
              </section>

              <section id="accounts" className="policy-section">
                <h2>3. Accounts & Credentials</h2>
                <p>
                  To access certain features of the Services, you must register for an account. You agree
                  to provide accurate, current, and complete information and to keep that information
                  up to date.
                </p>
                <h3>3.1 Credential Security</h3>
                <p>
                  You are responsible for safeguarding your account credentials and for any activity that
                  occurs under your account. You must notify us promptly at{' '}
                  <a href="mailto:security@juryview.com">security@juryview.com</a> of any unauthorized
                  access or suspected breach.
                </p>
                <h3>3.2 Multi-Factor Authentication</h3>
                <p>
                  Where multi-factor authentication or other security controls are made available, you
                  agree to use them as required by your subscription tier or applicable order form.
                </p>
              </section>

              <section id="license" className="policy-section">
                <h2>4. License & Subscription</h2>
                <p>
                  Subject to your compliance with these Terms and payment of applicable fees, JuryView
                  grants you a limited, non-exclusive, non-transferable, non-sublicensable right to access
                  and use the Services during the subscription term solely for your internal business
                  purposes.
                </p>
                <p>
                  Specific features, usage limits, seat counts, and any deliverables are described in your
                  applicable order form, statement of work, or subscription plan. In the event of a conflict,
                  an executed order form controls over these Terms with respect to its specific subject matter.
                </p>
              </section>

              <section id="acceptable-use" className="policy-section">
                <h2>5. Acceptable Use</h2>
                <p>You agree not to, and not to permit any Authorized User or third party to:</p>
                <ul>
                  <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, models, weights, or underlying algorithms of the Services;</li>
                  <li>Use the Services to develop or train any competing product, model, or service, or to benchmark the Services without our prior written consent;</li>
                  <li>Resell, sublicense, lease, time-share, or otherwise make the Services available to any third party except as expressly permitted;</li>
                  <li>Upload or transmit any content that is unlawful, infringing, defamatory, or that you do not have the right to share;</li>
                  <li>Upload personally identifying information of jurors, witnesses, or other natural persons in violation of applicable law, court order, or professional rules of conduct;</li>
                  <li>Interfere with or disrupt the integrity, security, or performance of the Services or any data they contain;</li>
                  <li>Attempt to gain unauthorized access to any portion of the Services or to systems or networks connected to the Services;</li>
                  <li>Use the Services to violate any applicable law, regulation, court order, or professional or ethical obligation.</li>
                </ul>
              </section>

              <section id="customer-data" className="policy-section">
                <h2>6. Customer Data & Confidentiality</h2>
                <p>
                  "Customer Data" means case materials, documents, prompts, queries, and other content you
                  or your Authorized Users submit to the Services. As between the parties, you retain all
                  right, title, and interest in and to your Customer Data.
                </p>
                <p>
                  You grant JuryView a limited, worldwide, non-exclusive license to host, process, transmit,
                  and display Customer Data solely as necessary to provide and support the Services and to
                  comply with applicable law. We do not use Customer Data to train publicly available
                  foundation models, and we do not sell Customer Data.
                </p>
                <p>
                  Our handling of personal information is further described in our{' '}
                  <Link to="/privacy">Privacy Policy</Link>, and our security practices are described in
                  our <Link to="/security">Security Policy</Link>, both of which are incorporated by
                  reference into these Terms.
                </p>
                <p>
                  You are responsible for ensuring that you have the rights and authorizations necessary
                  to submit Customer Data to the Services and that doing so does not violate applicable
                  law, court order, protective order, or professional obligation.
                </p>
              </section>

              <section id="ai-outputs" className="policy-section">
                <h2>7. AI Outputs & No Legal Advice</h2>
                <p>
                  The Services use artificial intelligence and statistical models to generate simulations,
                  predictions, scoring, and other outputs ("Outputs"). Outputs are probabilistic estimates
                  based on the inputs provided and on calibration data. They are not facts, are not
                  guarantees of any outcome, and do not constitute legal advice.
                </p>
                <p>
                  Outputs are provided for use by qualified legal professionals as one input among many in
                  case strategy and decision-making. You are solely responsible for any decisions or actions
                  you take based on Outputs and for ensuring that your use of the Services complies with
                  all applicable laws, court rules, and professional rules of conduct, including those
                  governing the use of artificial intelligence in legal practice.
                </p>
                <p>
                  No attorney-client relationship is formed between you and JuryView by your use of the
                  Services.
                </p>
              </section>

              <section id="ip" className="policy-section">
                <h2>8. Intellectual Property</h2>
                <p>
                  The Services, including all software, models, weights, datasets, documentation, designs,
                  and trademarks, are owned by JuryView or its licensors and are protected by intellectual
                  property and other laws. Except for the limited rights expressly granted in these Terms,
                  no rights or licenses are granted to you, whether by implication, estoppel, or otherwise.
                </p>
                <p>
                  If you provide feedback, suggestions, or ideas about the Services ("Feedback"), you grant
                  JuryView a perpetual, irrevocable, royalty-free, worldwide license to use, modify, and
                  incorporate Feedback into the Services without obligation to you.
                </p>
              </section>

              <section id="fees" className="policy-section">
                <h2>9. Fees & Payment</h2>
                <p>
                  Fees, billing frequency, and payment terms are set forth in your applicable order form
                  or subscription plan. Unless otherwise stated, all fees are non-refundable, are quoted
                  and payable in U.S. dollars, and are exclusive of taxes (other than taxes on JuryView's
                  net income).
                </p>
                <p>
                  Late payments may accrue interest at the lesser of 1.5% per month or the maximum rate
                  permitted by law. We may suspend the Services for non-payment after providing reasonable
                  notice.
                </p>
              </section>

              <section id="term" className="policy-section">
                <h2>10. Term & Termination</h2>
                <p>
                  These Terms remain in effect for so long as you have an active subscription or otherwise
                  use the Services.
                </p>
                <h3>10.1 Termination for Cause</h3>
                <p>
                  Either party may terminate these Terms (or the affected order form) for material breach
                  if the breach is not cured within thirty (30) days after written notice. We may suspend
                  or terminate your access immediately if we reasonably determine that your use of the
                  Services poses a security risk, violates the law, or violates Section 5 (Acceptable Use).
                </p>
                <h3>10.2 Effect of Termination</h3>
                <p>
                  Upon termination, your right to access and use the Services ends. We will make Customer
                  Data available for export for a limited period, after which we may delete it in accordance
                  with our retention practices and applicable law. Sections that by their nature should
                  survive termination (including confidentiality, IP, disclaimers, limitation of liability,
                  indemnification, and governing law) will survive.
                </p>
              </section>

              <section id="warranties" className="policy-section">
                <h2>11. Warranty Disclaimer</h2>
                <p>
                  EXCEPT AS EXPRESSLY SET FORTH IN AN EXECUTED ORDER FORM, THE SERVICES AND OUTPUTS ARE
                  PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, JURYVIEW
                  DISCLAIMS ALL WARRANTIES, EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF
                  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, AND
                  AVAILABILITY. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE
                  OR THAT OUTPUTS WILL BE ACCURATE OR PREDICTIVE OF ANY ACTUAL LEGAL OUTCOME.
                </p>
              </section>

              <section id="liability" className="policy-section">
                <h2>12. Limitation of Liability</h2>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE TO THE OTHER FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST
                  PROFITS, LOST REVENUE, LOST GOODWILL, OR LOSS OF DATA, ARISING OUT OF OR RELATED TO THESE
                  TERMS OR THE SERVICES, REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF ADVISED OF THE
                  POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p>
                  EACH PARTY'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS WILL NOT
                  EXCEED THE FEES PAID OR PAYABLE BY YOU TO JURYVIEW FOR THE SERVICES IN THE TWELVE (12)
                  MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO LIABILITY.
                </p>
                <p>
                  THE LIMITATIONS IN THIS SECTION DO NOT LIMIT YOUR PAYMENT OBLIGATIONS, A PARTY'S
                  INDEMNIFICATION OBLIGATIONS, OR LIABILITY FOR FRAUD, GROSS NEGLIGENCE, OR WILLFUL
                  MISCONDUCT.
                </p>
              </section>

              <section id="indemnification" className="policy-section">
                <h2>13. Indemnification</h2>
                <p>
                  You agree to defend, indemnify, and hold harmless JuryView and its affiliates, officers,
                  directors, employees, and agents from and against any claims, damages, losses, and
                  expenses (including reasonable attorneys' fees) arising out of or related to (a) your
                  Customer Data; (b) your use of the Services in violation of these Terms or applicable
                  law; or (c) your violation of any third-party rights.
                </p>
                <p>
                  JuryView will defend you against any third-party claim alleging that the Services, when
                  used as authorized, infringe such third party's intellectual property rights, and will
                  pay damages finally awarded against you on that claim, subject to standard exclusions
                  and procedures set forth in your order form.
                </p>
              </section>

              <section id="governing-law" className="policy-section">
                <h2>14. Governing Law & Disputes</h2>
                <p>
                  These Terms are governed by the laws of the State of Delaware, without regard to its
                  conflict-of-laws principles. The parties consent to the exclusive jurisdiction of the
                  state and federal courts located in Delaware for any dispute arising out of or related
                  to these Terms, except that either party may seek injunctive or equitable relief in any
                  court of competent jurisdiction to protect its intellectual property or confidential
                  information.
                </p>
                <p>
                  These Terms, together with any executed order form and the policies incorporated by
                  reference, constitute the entire agreement between the parties regarding the Services
                  and supersede all prior or contemporaneous agreements on that subject.
                </p>
              </section>

              <section id="changes" className="policy-section">
                <h2>15. Changes to These Terms</h2>
                <p>
                  We may update these Terms from time to time to reflect changes in our Services, our
                  business, or applicable law. If we make material changes, we will provide notice through
                  the Services or by email to registered account contacts before the changes take effect.
                  Your continued use of the Services after the effective date constitutes acceptance of
                  the updated Terms.
                </p>
              </section>

              <section id="contact" className="policy-section">
                <h2>16. Contact Us</h2>
                <p>
                  If you have questions about these Terms, please contact us:
                </p>
                <div className="contact-info">
                  <p><strong>JuryView, Inc.</strong></p>
                  <p>Legal Department</p>
                  <p>Email: <a href="mailto:legal@juryview.com">legal@juryview.com</a></p>
                </div>
              </section>

              <div className="policy-footer">
                <p>
                  By using the Services, you also agree to our{' '}
                  <Link to="/privacy">Privacy Policy</Link> and{' '}
                  <Link to="/security">Security Policy</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
